/**
 * Persisted user settings (PLAN.md §9). Stored in localStorage with a schema
 * version; unknown/invalid stored values fall back to defaults.
 */

export type WindowName = 'rect' | 'hann' | 'hamming' | 'blackman' | 'kaiser';
export type Theme = 'system' | 'light' | 'dark';

export interface SettingsData {
  frameSize: number;
  window: WindowName;
  hop: number;
  dbMin: number;
  dbMax: number;
  freqAxis: 'lin' | 'log';
  calibrationDb: number;
  units: 'dBFS' | 'dBSPL';
  firWindow: 'rect' | 'hann' | 'kaiser';
  firKaiserBeta: number;
  firTaps: number;
  wolaOverlap: 0.5 | 0.75;
  theme: Theme;
}

export const DEFAULT_SETTINGS: SettingsData = {
  frameSize: 2048,
  window: 'hann',
  hop: 1024,
  dbMin: -120,
  dbMax: 0,
  freqAxis: 'log',
  calibrationDb: 96,
  units: 'dBFS',
  firWindow: 'kaiser',
  firKaiserBeta: 6,
  firTaps: 2049,
  wolaOverlap: 0.5,
  theme: 'system',
};

const KEY = 'soundlab.settings';
const SCHEMA = 1;

export function mergeSettings(stored: unknown): SettingsData {
  const out: SettingsData = { ...DEFAULT_SETTINGS };
  if (!stored || typeof stored !== 'object') return out;
  const s = stored as { schema?: number; data?: Record<string, unknown> };
  if (s.schema !== SCHEMA || !s.data) return out;
  for (const k of Object.keys(DEFAULT_SETTINGS) as (keyof SettingsData)[]) {
    const v = s.data[k];
    if (v !== undefined && typeof v === typeof DEFAULT_SETTINGS[k]) {
      (out as unknown as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

function load(): SettingsData {
  try {
    return mergeSettings(JSON.parse(localStorage.getItem(KEY) ?? 'null'));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export const settings = $state<SettingsData>(load());

$effect.root(() => {
  $effect(() => {
    const snapshot = $state.snapshot(settings);
    try {
      localStorage.setItem(KEY, JSON.stringify({ schema: SCHEMA, data: snapshot }));
    } catch {
      /* storage full or disabled: settings just won't persist */
    }
  });
});
